// services/frontend/src/components/composed/PhotoRatingControl/PhotoRatingControl.tsx
// Feature 649: Component that encapsulates photo rating for both photographers and critics

import { useCallback } from 'react';
import { Heading } from '../../atoms/Heading/Heading';
import { Text } from '../../atoms/Text/Text';
import { StarRating } from '../StarRating';
import { RatingSummary } from '../RatingSummary';
import { useMyRatingForPhoto, useSubmitRating } from '../../../graphql/hooks/usePhotoRatingsGraphQL';

export interface PhotoRatingControlProps {
  /** The photo being rated */
  photoId: string;
  /** The critique session ID */
  sessionId: string;
  /** Whether the current user is the photographer (sees summary) or a critic (can rate) */
  isPhotographer: boolean;
}

export function PhotoRatingControl({
  photoId,
  sessionId,
  isPhotographer,
}: PhotoRatingControlProps) {
  // Only fetch user's rating if they're a critic (not the photographer)
  const { myRating, loading: ratingLoading } = useMyRatingForPhoto(
    photoId,
    sessionId,
    { skip: isPhotographer }
  );

  const { submitRating, loading: submitRatingLoading } = useSubmitRating();

  const handleRatingChange = useCallback(async (rating: number) => {
    try {
      await submitRating(photoId, sessionId, rating);
    } catch {
      // Error is logged in the hook
    }
  }, [photoId, sessionId, submitRating]);

  return (
    <section className="pb-4 border-b border-border">
      <div className="flex items-center justify-between">
        <Heading level="h5">Rate This Photo</Heading>
        {isPhotographer ? (
          <RatingSummary photoId={photoId} sessionId={sessionId} />
        ) : (
          <StarRating
            value={myRating?.rating ?? null}
            onChange={handleRatingChange}
            loading={ratingLoading || submitRatingLoading}
            aria-label="Rate this photo"
          />
        )}
      </div>
      {!isPhotographer && myRating && (
        <Text variant="muted" className="text-xs mt-1">
          You rated this photo {myRating.rating} star{myRating.rating !== 1 ? 's' : ''}
        </Text>
      )}
    </section>
  );
}
